import React, { useState, useEffect, useCallback } from 'react';
import { Delete, X } from 'lucide-react';

export default function PinKeypad({
  pinLength = 4,
  onComplete,
  error = '',
  disabled = false,
  title = 'Enter Security PIN',
  subtitle = 'Enter your numeric passkey to continue',
}) {
  const [digits, setDigits] = useState('');

  const handleDigit = useCallback(
    (digit) => {
      if (disabled) return;
      if (navigator.vibrate) navigator.vibrate(20);
      setDigits((prev) => {
        if (prev.length >= pinLength) return prev;
        const next = prev + digit;
        if (next.length === pinLength && onComplete) {
          setTimeout(() => onComplete(next), 60);
        }
        return next;
      });
    },
    [disabled, pinLength, onComplete]
  );

  const handleBackspace = useCallback(() => {
    if (disabled) return;
    if (navigator.vibrate) navigator.vibrate(15);
    setDigits((prev) => prev.slice(0, -1));
  }, [disabled]);

  const handleClear = useCallback(() => {
    if (disabled) return;
    setDigits('');
  }, [disabled]);

  // Handle physical keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [disabled, handleDigit, handleBackspace, handleClear]);

  // Clear digits when an error occurs so user can re-try
  useEffect(() => {
    if (error) {
      setDigits('');
    }
  }, [error]);

  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'backspace'],
  ];

  return (
    <div className="cv-pin-keypad-container animate-fade-in">
      {title && <div className="cv-pin-title">{title}</div>}
      {subtitle && <div className="cv-pin-subtitle">{subtitle}</div>}

      {/* PIN Dots Indicator */}
      <div className={`cv-pin-dots-row ${error ? 'animate-shake' : ''}`}>
        {Array.from({ length: pinLength }).map((_, i) => {
          const isFilled = i < digits.length;
          const isCurrent = i === digits.length;
          return (
            <div
              key={i}
              className={`cv-pin-dot ${isFilled ? 'filled' : ''} ${isCurrent ? 'current' : ''} ${error ? 'error' : ''}`}
            />
          );
        })}
      </div>

      {error && <div className="cv-pin-error-text animate-shake">{error}</div>}

      {/* Numeric Touch Keypad */}
      <div className="cv-pin-grid">
        {keys.map((row, rowIdx) => (
          <div key={rowIdx} className="cv-pin-grid-row">
            {row.map((k) => {
              if (k === 'clear') {
                return (
                  <button
                    key="clear"
                    type="button"
                    className="cv-pin-btn cv-pin-btn-action"
                    onClick={handleClear}
                    disabled={disabled || digits.length === 0}
                    title="Clear"
                  >
                    <X size={18} />
                  </button>
                );
              }
              if (k === 'backspace') {
                return (
                  <button
                    key="backspace"
                    type="button"
                    className="cv-pin-btn cv-pin-btn-action"
                    onClick={handleBackspace}
                    disabled={disabled || digits.length === 0}
                    title="Delete"
                  >
                    <Delete size={18} />
                  </button>
                );
              }
              return (
                <button
                  key={k}
                  type="button"
                  className="cv-pin-btn"
                  onClick={() => handleDigit(k)}
                  disabled={disabled}
                >
                  <span>{k}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
